import { useQuery, useQueryClient } from 'react-query';
import { useRouter } from 'next/router';
import { setHeaders, getClient, refreshClient } from '@graphql/graphql-client/graphClient';
import { useCurrentUserQuery, User } from '@graphql/generated-graphql';
import { EnrollmentQueryKeys } from '../useEnrollmentsWithMapping';

export const SessionKeys = {
    key: 'session',
};

export const UserQueryKeys = {
    currentUser: 'CurrentUser',
    realUser: 'RealUser',
};
export async function fetchSession() {
    const res = await fetch('/api/session');
    const session = await res.json();
    if (res.ok) {
        if (Object.keys(session).length) {
            return session;
        }
    }
    throw session;
    return null;
}

export function useSession(
    { required, redirectTo = '/auth/signin', queryConfig = {} } = {} as any,
) {
    const router = useRouter();
    const query = useQuery([SessionKeys.key], fetchSession, {
        ...queryConfig,
        onSettled(data, error) {
            if (queryConfig.onSettled) queryConfig.onSettled(data, error);
            if (data || !required) return;
            router.push(redirectTo);
        },
    });
    return [query.data, query.status === 'loading', query.isError];
}

export function useUserSession({ queryConfig = {} } = {} as any) {
    const queryClient = useQueryClient();
    const [session, loading, isError] = useSession({
        required: true,
        redirectTo: '/auth/signin',
        queryConfig: {
            staleTime: 60 * 1000 * 60 * 1, // 1 hour
            refetchInterval: 10 * 60 * 1000 * 1, // 10 minutes
            onSettled: (data: any, error: unknown) => {
                if (data?.user) {
                    if (queryConfig.onSettled) queryConfig.onSettled(data, error);
                    queryClient.invalidateQueries([UserQueryKeys.currentUser]);
                    queryClient.invalidateQueries([UserQueryKeys.realUser]);
                    queryClient.invalidateQueries([EnrollmentQueryKeys.key]);
                    queryClient.invalidateQueries(['AccountUsers']);
                } else {
                    if ((error as any)?.message === 'session expired') {
                        refreshClient();
                    }
                }
            },
            retry: (ct: number, error: any) => {
                if (error?.message === 'no active session') {
                    return false;
                }
                if (ct === 2) {
                    return false;
                }
                return false;
            },
        },
    });

    const { data: { me } = {}, status } = useCurrentUserQuery(getClient('users'), undefined, {
        enabled: !!session && !loading,
        /**
         * We have other ways to refetch.
         * We can invalidate the CurrentUser query to force this to run
         */
        staleTime: 60 * 1000 * 60 * 1, // Should be 1 hour,
    });

    return [me ?? undefined, loading, isError] as [User, Boolean, Boolean];
}
